import { AfterViewInit, Component, HostListener, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Chart, ChartDataset, ChartOptions, ChartType, TooltipItem } from 'chart.js';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons/faCaretDown';
import { faSquare } from '@fortawesome/free-solid-svg-icons/faSquare';
import * as moment from 'moment';
import 'chartjs-adapter-moment';
import { default as Annotation } from 'chartjs-plugin-annotation';
import { GlobalState } from '../../global.state';
import { environment } from '../../../environments/environment';
import { Util } from '../../utils/util.service';
import { MarketInfoUtil } from '../../utils/market-info.util';
import { Logger } from '../../utils/logger.service';
import { IconDefinition } from '@fortawesome/free-regular-svg-icons';
import { Subject, takeUntil } from 'rxjs';

export interface CurrentVixStats {
  diffFromHistorical?: number;
  diffFromHistoricalPercent?: number;
}

@Component( {
  selector: 'rip-vix-chart',
  template: `
    <nb-card [nbSpinner]="loadingCurrentVix && loadingHistoricalVix">
      <nb-card-header style="position: relative;">
        <div id="vix-card-header" class="flexing space-between center-vertically">
          <div>
            <div class="market-info-card-title">
              CBOE Volatility Index<sup>®</sup> (VIX<sup>®</sup> Index)
            </div>
          </div>
          <div>
            <mat-button-toggle-group #dateRadio id="date-range-button-group"
                                     [(value)]="marketInfoUtil.currentDateRangeOption"
                                     (valueChange)="changeSeriesRange()">
              <mat-button-toggle *ngFor="let option of marketInfoUtil.dateRangeRadioOptions" [value]="option.value">
                {{ option.label }}
              </mat-button-toggle>
            </mat-button-toggle-group>
          </div>
        </div>
      </nb-card-header>
      <nb-card-body>
        <div id="vix-chart-group">
          <div id="vix-chart-container" [nbSpinner]="loadingHistoricalVix">
            <nb-badge *ngIf="marketInfoUtil.historicalVixError" text="Error Loading Historical Data. Click here to try again"
                      position="top left" (click)="getHistoricalVix()"
                      class="button-badge"
                      status="danger">
            </nb-badge>
            <div id="historical-high-label">
              <span>*All Time High VXO®: {{ actualHigh }} (10/19/1987)</span>
            </div>
            <div id="vix-chart-canvas-container">
              <canvas baseChart #vixChartCanvas width="100%"
                      [datasets]="vixChartDataSets"
                      [labels]="vixChartLabels"
                      [options]="vixChartOptions"
                      [legend]="false"
                      [type]="vixChartType"
                      (error)="chartError($event)"
                      *ngIf="vixChartDataSets.length > 0"></canvas>
            </div>
          </div>
          <div id="vix-snapshot-container">
            <nb-card [nbSpinner]="loadingCurrentVix">
              <nb-card-header style="position: relative;">
                <nb-badge *ngIf="marketInfoUtil.currentVixError"
                          text="Error Loading Current Data. Click here to try again"
                          position="top left" (click)="getCurrentVix()"
                          class="button-badge"
                          status="danger">
                </nb-badge>
                <div>Today</div>
                <div class="footnote">
                  {{ marketInfoUtil.currentVixQuote?.asOfDate | date : 'medium' }}
                </div>
              </nb-card-header>
              <nb-card-body>
                <div id="vix-snapshot" *ngIf="marketInfoUtil.snapshot">

                  <div style="text-align: center;">
                    <div class="snapshot-header" *ngIf="!marketInfoUtil.customVix">
                      Current VIX<sup>®</sup>
                    </div>
                    <div class="snapshot-header" *ngIf="marketInfoUtil.customVix">
                      Your Short-Term Market Volatility
                    </div>
                    <div [style.border-color]="marketInfoUtil.snapshot?.currentColor" class="current-indicator">
                      {{ marketInfoUtil.snapshot?.current | ripDecimalPipe : '2-2' }}
                    </div>
                  </div>

                  <div>
                    <div class="snapshot-header">
                      {{ marketInfoUtil.snapshot?.diffFromLTAV | ripDecimalPipe : '2-2' }}
                      / {{ marketInfoUtil.snapshot?.percentDiffFromLTAV | ripPercentPipe : '2-2' }}
                      <b>
                        <span *ngIf="marketInfoUtil.snapshot?.diffDirection === 'up'">ABOVE</span>
                        <span *ngIf="marketInfoUtil.snapshot?.diffDirection === 'down'">BELOW</span>
                        <span *ngIf="marketInfoUtil.snapshot?.diffFromLTAV === 0">DIFFERENCE FROM</span>
                      </b>
                    </div>
                    <div *ngIf="marketInfoUtil.longTermStandardDeviation === defaultLongTermMarketVolatility">
                      The Long-Term Market Volatility
                    </div>
                    <div *ngIf="marketInfoUtil.longTermStandardDeviation !== defaultLongTermMarketVolatility">
                      Your Long-Term Market Volatility
                    </div>
                  </div>
                </div>
              </nb-card-body>
            </nb-card>
          </div>
        </div>
      </nb-card-body>
      <nb-card-footer>
        <div class="flexing space-between vix-chart-color-legend">
          <span><fa-icon [icon]="faSquare" style="color: #49a1e0; "></fa-icon>Extremely Low (0-10)</span>
          <span><fa-icon [icon]="faSquare" style="color: #207bbc; "></fa-icon>Low (10-13)</span>
          <span><fa-icon [icon]="faSquare" style="color: #ffa139; "></fa-icon>Moderate (13-24)</span>
          <span><fa-icon [icon]="faSquare" style="color: #ff452b; "></fa-icon>High (24-48)</span>
          <span><fa-icon [icon]="faSquare" style="color: #993333; "></fa-icon>Extremely High (48+)</span>
        </div>
        <div class="market-info-blurb">
          <div>
            <b>The CBOE Volatility Index<sup>®</sup>, or VIX<sup>®</sup></b>, is a real-time market index representing
            the market’s expectation for volatility over the coming 30 days (forward-looking). Investors use the VIX as
            a measure of the level of risk, fear, or stress in the market when making investment decisions.
          </div>
          <div>
            Learn more about <a (click)="openVixLink()" class="rip-link">VIX</a>
          </div>
        </div>
      </nb-card-footer>
      <nb-card-footer>
        <div style="display: flex; justify-content: flex-start" class="footnote">
          *VXO® All Time High sourced from
          <a (click)="openCBOELink()" class="rip-link" style="margin-left: 5px;">CBOE</a>
        </div>
      </nb-card-footer>
    </nb-card>
  `,
  styleUrls: [ './vix-chart.component.scss' ],
} )

export class VixChartComponent implements AfterViewInit, OnDestroy {

  faCaretUp: IconDefinition = faCaretUp;
  faCaretDown: IconDefinition = faCaretDown;
  faSquare: IconDefinition = faSquare;

  private readonly onDestroy = new Subject<void>();

  get defaultLongTermMarketRiskPremium(): number {
    return MarketInfoUtil.defaultLongTermRiskPremium;
  };

  get defaultLongTermMarketVolatility(): number {
    return MarketInfoUtil.defaultLongTermStandardDeviation;
  }

  @ViewChild( 'tooltipTemplate' ) tooltipTemplate: TemplateRef<any>;

  @HostListener( 'window:resize', [ '$event' ] )
  onResize( /*event: Event*/ ) {
    // Logger.info( 'there was a resize event' );
    if ( this.chartObject ) {
      this.chartObject.chart.update();
    }
  }

  today: Date = new Date();


  vixSeries: any[] = this.marketInfoUtil.vixSeries;
  daysBack: number = 252 * 5; // 5 years of trading days


  vixSeriesStats: any = this.marketInfoUtil.vixSeriesStats
  ;
  currentVixStats: CurrentVixStats = {};
  // chartjs implementation  -------------------------------------------------------------------------------------------
  vixChartDataSets: any[] = [];
  vixChartLabels: any = [];
  vixChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,

    layout: {
      padding: {
        bottom: 20,
        right: 0,
      },
    },
    scales: {
      x: {
        // id: 'time',
        type: 'time',
        time: {
          unit: 'year',
          displayFormats: {
            'hour': 'MMM DD',
            'day': 'MMM DD',
            'week': 'MMM DD',
            'month': 'MMM DD',
          },
        },
        grid: {
          display: true,
        },
        ticks: {
          maxTicksLimit: 10,
        },
      },
      y: {
        // id: 'vix',
        type: 'linear',
        position: 'left',
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        callbacks: {
          // tooltipItems is an array. one item for each data set (but we only have one in this chart, so we can use [0])
          title: ( tooltipItems: ( TooltipItem<ChartType> )[] ) => {
            // should only be one tooltipItem
            const tooltip = tooltipItems[ 0 ];
            const labelArray = tooltip.label.split( ',' ); // this should be a fully formatted date like Aug 24, 2015, 12:00:00 am, so we want to get
                                                           // rid of the timestamp
            labelArray.pop(); // this will pop the timestamp off the end
            return labelArray.join( ', ' ); // then put the string back together and return
          },
          // tooltipItem is an object here, because it is called for each dataset at the point on the axis
          labelColor: ( tooltipItem: TooltipItem<ChartType> /*, chart*/ ) => {
            return {
              borderColor: '#a09f9f',
              backgroundColor: MarketInfoUtil.getColor( ( tooltipItem.raw as number ) ),
            };
          },
          label: ( tooltipItem ) => {
            return tooltipItem.formattedValue;
          },
        },
      },
      title: {
        display: false,
        text: 'VIX',
        color: '#79afd7',
        font: {
          weight: 'bolder',
          size: this._state.chartTitleHeight,
        },

      },
      annotation: {
        // drawTime: 'afterDatasetDraw',
        annotations: {},
      },
      /*legend: {

       }*/

    },


  };
  vixChartType: ChartType = 'line';

  chartObject: any;

  loadingHistoricalVix: boolean = true;
  loadingCurrentVix: boolean = true;

  // --------------------------------------------------------------------------------------------------------------

  readMore: boolean = false;

  subscriberName: string = 'vixChartComponent';

  constructor( private _state: GlobalState,
               public snackBar: MatSnackBar,
               public marketInfoUtil: MarketInfoUtil ) {

    this.vixChartOptions.scales.x.ticks.maxTicksLimit = this.marketInfoUtil.deviceIsMobile ? 5 : 10;


    if ( environment.env !== 'prod' ) {
      window[ 'ripsaw_vixChart' ] = this;
    }

    _state.subscribe( 'market.calc.settings.changed', ( settings ) => {
      this.marketInfoUtil.marketSettingsChanged( settings );
      this.getCurrentVix();
    }, this.subscriberName );

    Chart.register( Annotation );

    this.loadingCurrentVix = this.marketInfoUtil.loadingCurrentVix.value;
    this.marketInfoUtil.loadingCurrentVix.pipe( takeUntil( this.onDestroy ) )
      .subscribe( {
        next: ( loading: boolean ) => {
          this.loadingCurrentVix = loading;
          if ( !loading ) {
            this.changeSeriesRange();
          }
        },
      } );
    this.loadingHistoricalVix = this.marketInfoUtil.loadingHistoricalVix.value;

    this.marketInfoUtil.loadingHistoricalVix.pipe( takeUntil( this.onDestroy ) )
      .subscribe( {
        next: ( loading: boolean ) => {
          this.loadingHistoricalVix = loading;
          if ( !loading ) {
            this.changeSeriesRange();
          }
        },
      } );
  }

  ngAfterViewInit() {
    setTimeout( () => {
      this.getVixData();
    } );
  }

  ngOnDestroy() {
    this._state.unsubscribe( [ 'market.calc.settings.changed' ].join( ' | ' ), this.subscriberName );
  }

  getVixData() {
    if ( !this.marketInfoUtil.currentVixQuote?.price && this._state.globalVars.firstAccountPullComplete ) {
      this.marketInfoUtil.getHistoricalVix();
      this.getCurrentVix();
    }
  }

  getCurrentVix() {
    if ( !this.marketInfoUtil.currentVixQuote?.price && this._state.globalVars.firstAccountPullComplete ) {
      this.marketInfoUtil.getCurrentVix();
    }
  }

  getHistoricalVix(): void {
    if ( !this.marketInfoUtil.currentVixQuote?.price && this._state.globalVars.firstAccountPullComplete ) {
      this.marketInfoUtil.getHistoricalVix();
    }
  }

  chartError( event ) {
    Logger.log( event );
  }

  changeSeriesRange() {
    if ( this.marketInfoUtil.currentDateRangeOption ) {
      let timeUnit;
      switch ( this.marketInfoUtil.currentDateRangeOption.unit ) {
        case 'D':
          this.daysBack = this.marketInfoUtil.currentDateRangeOption.unitsBack;
          timeUnit = 'day';
          break;
        case 'W':
          this.daysBack = 7 * this.marketInfoUtil.currentDateRangeOption.unitsBack;
          timeUnit = 'day';
          break;
        case 'M':
          this.daysBack = 20 * this.marketInfoUtil.currentDateRangeOption.unitsBack;
          timeUnit = 'week';
          break;
        case 'Y':
          this.daysBack = 252 * this.marketInfoUtil.currentDateRangeOption.unitsBack;
          timeUnit = 'year';
          break;
        default:
          this.daysBack = this.vixSeries.length - 1;
          timeUnit = 'year';
          break;

      }
      this.vixChartOptions.scales.x[ 'time' ].unit = timeUnit;
      this.setVixStats();
      this.setupVixChart();
    }
  }

  /*
   checkForBothDataPieces() {
   this.bothVixDataPiecesReturned = this.vixSeries?.length > 0 && this.currentVixQuote !== undefined;

   this.setVixStats();
   this.changeSeriesRange();
   // }
   }*/

  getGradientStroke: Function = ( context ) => {
    const chart = context.chart;
    const { ctx, chartArea } = chart;

    if ( !chartArea ) {
      // This case happens on initial chart load
      return;
    }

    const gradientStroke = ctx.createLinearGradient( 0, chartArea.bottom, 0, chartArea.top );
    gradientStroke.addColorStop( .1, MarketInfoUtil.getColor( 10 ) );
    gradientStroke.addColorStop( .13, MarketInfoUtil.getColor( 13 ) );
    gradientStroke.addColorStop( .24, MarketInfoUtil.getColor( 24 ) );
    gradientStroke.addColorStop( .48, MarketInfoUtil.getColor( 48 ) );
    gradientStroke.addColorStop( 1, MarketInfoUtil.getColor( 1000 ) );
    return gradientStroke;
  };


  setupVixChart() {
    this.vixChartDataSets = [];
    this.vixChartLabels = [];
    // chartjs implemenation
    const vixSet: ChartDataset = {
      data: [],
      borderColor: ( context ) => {
        return this.getGradientStroke( context );
      },
      backgroundColor: ( context ) => {
        return this.getGradientStroke( context );
      },
      label: 'VIX',
      pointRadius: 0,
      pointHoverRadius: 1,
      // fill: '+1', // if you want to fill to the horizontal line
      fill: false,
      borderWidth: 2,
      tension: 0,
    };
    if ( this.vixSeries?.length > 0 ) {
      for ( let i = this.daysBack; i >= 0; i-- ) {
        if ( this.vixSeries?.[ i ] ) {
          vixSet.data.push( this.vixSeries[ i ].adjusted_close );
          this.vixChartLabels.push( moment( this.vixSeries[ i ].date ) ); //.format( 'DD/MM/YYYY' ) );
        }
      }

      if ( this.marketInfoUtil.currentVixQuote?.price ) {
        vixSet.data.push( this.marketInfoUtil.currentVixQuote?.price );

        this.vixChartLabels.push( moment( this.today ) ); //.format( 'DD/MM/YYYY' ) );
      }

      this.vixChartDataSets = [ vixSet ];
    }
  }

  calcRiskPremium( vix ) {
    return ( 7.2 / ( Math.pow( 17.2, 2 ) ) * Math.pow( vix, 2 ) );
  }

  actualHigh = 150.19;

  setVixStats() {
    // noinspection TypeScriptValidateJSTypes
    this.vixChartOptions.plugins.annotation.annotations[ 'high' ] = {
      type: 'line',
      scaleID: 'y',
      value: this.vixSeriesStats?.historical_high?.value,
      // yMax: this.vixSeriesStats?.historical_high?.value,
      borderColor: '#993333',
      borderWidth: 2,
      label: {
        display: true,
        position: 'start',
        yAdjust: -13,
        // enabled: true,
        color: 'white',
        backgroundColor: '#993333',
        content: `VIX®: ${ this.vixSeriesStats?.historical_high?.value } (${ moment( this.vixSeriesStats?.historical_high?.date ).format( 'MM/DD/YYYY' ) })`,
        font: {
          weight: 'bold',
        },
      },
    };
    this.vixChartOptions.plugins.annotation.annotations[ 'avg' ] = {
      type: 'line',
      scaleID: 'y',
      value: this.marketInfoUtil.longTermStandardDeviation,
      borderColor: '#ffa139',
      borderWidth: 3,
      label: {
        position: 'start',
        display: true,
        xAdjust: 0,
        yAdjust: 13,
        backgroundColor: '#ffa139',
        color: 'white',
        content: `Long-Term Vol: ${ this.marketInfoUtil.longTermStandardDeviation }`,
        font: {
          weight: 'bold',
        },
      },
    };
    this.vixChartOptions.plugins.annotation.annotations[ 'low' ] = {
      type: 'line',
      scaleID: 'y',
      value: this.vixSeriesStats?.historical_low?.value,
      borderColor: '#49a1e0',
      borderWidth: 3,
      label: {
        position: 'start',
        yAdjust: 13,
        color: 'white',
        backgroundColor: '#49a1e0',
        display: true,
        content: `VIX®: ${ this.vixSeriesStats?.historical_low?.value } (${ moment( this.vixSeriesStats?.historical_low?.date ).format( 'MM/DD/YYYY' ) })`,
        font: {
          weight: 'bold',
        },
      },
    };
    this.chartObject?.update();
  }

  onSelect( data ): void {
    Logger.log( 'Item clicked', JSON.parse( JSON.stringify( data ) ) );
  }

  onActivate( data ): void {
    Logger.log( 'Activate', JSON.parse( JSON.stringify( data ) ) );
  }

  onDeactivate( data ): void {
    Logger.log( 'Deactivate', JSON.parse( JSON.stringify( data ) ) );
  }

  openVixLink() {
    Util.openExternalUrl( 'https://www.investopedia.com/terms/v/vix.asp' );
  }

  openCBOELink() {
    Util.openExternalUrl( 'https://www.cboe.com/tradable_products/vix/vix_historical_data/' );
  }
}
